// Modules
import pick from "lodash.pick";
import { addYears, isDate, startOfDay, endOfDay } from "date-fns";
// Helpers
import { EVENT_CANCELED, EVENT_FREE_SLOT, EVENT_SESSION, STATUS_IS_ACTIVE } from "@/helpers/constants";
import { getEventDates } from "@/components/UITemplate/views/calendar/helpers/rrule";
import { addExceptionDates } from "@/components/UITemplate/views/calendar/helpers/calendar";

/**
 * Extracts the value of a specified field in an object.
 * @param {String} field - The field name to extract.
 * @param {Object} objValues - The object from which to extract the field value.
 * @returns {Array} - An array containing the values of the specified field in the object.
 */

export const filterAndFormatTherapistEvents = (
    substitutes,
    bookedEvents,
    getColorByIndex,
    assignedTherapist,
    selectedTherapists,
    allAvailableTherapists
) => {
    if (!allAvailableTherapists || !allAvailableTherapists.length) {
        return { events: [], therapists: [] };
    }

    const userTherapist = allAvailableTherapists.find(({ _id }) => _id === assignedTherapist?._id);

    const colorIndexMap = substitutes?.reduce((map, therapist, index) => {
        const color = getColorByIndex(index + 1);
        return { ...map, [therapist._id]: color };
    }, {});

    if (userTherapist) {
        const color = getColorByIndex(0);
        colorIndexMap[userTherapist._id] = color;
    }

    const therapists = substitutes.map(therapist => ({
        ...therapist,
        color: colorIndexMap[therapist._id]?.free_slot
    }));
    if (substitutes.length > 0) {
        therapists.unshift({
            _id: userTherapist._id,
            name: userTherapist.name,
            color: colorIndexMap[userTherapist._id]?.free_slot
        });
    }

    const allTherapists = [
        userTherapist,
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...allAvailableTherapists?.filter(
            (/** @type {{ _id: string | number; }} */ therapist) =>
                therapist !== userTherapist && selectedTherapists[therapist._id]
        )
    ];

    const events = allTherapists.flatMap(therapist => {
        const color = therapist === userTherapist ? getColorByIndex(0) : colorIndexMap[therapist._id];

        const therapistEvents = (therapist.events || [])
            .filter(event => [EVENT_FREE_SLOT].includes(event.type) && event[STATUS_IS_ACTIVE] !== false)
            .map(event => {
                let exDates = [];

                if (event.type === EVENT_FREE_SLOT) {
                    const matchingSessions = therapist?.events?.filter(
                        session => session.type === EVENT_SESSION && event?.session_ids?.includes(session.id)
                    );

                    matchingSessions.forEach(session => {
                        const { rrule } = session || {};
                        const { dtstart, until } = rrule || {};

                        const currentDate = new Date();
                        const oneYearLater = addYears(currentDate, 1);

                        const startFrom = dtstart && startOfDay(new Date(dtstart));
                        const untilDate = until && endOfDay(new Date(until));
                        const startTo = rrule ? (isDate(untilDate) ? untilDate : endOfDay(oneYearLater)) : undefined;

                        const dates = getEventDates({ event: session, startFrom, startTo });
                        exDates.push(...dates);
                    });
                }

                if (exDates.length > 0) {
                    addExceptionDates(event, exDates);
                }
                return {
                    ...event,
                    color: therapist === userTherapist ? getColorByIndex(0)?.free_slot : color?.free_slot,
                    user: pick(therapist, ["_id", "name", "email", "meeting_link"])
                };
            });

        const therapistBookedEvents = bookedEvents
            .filter(
                event =>
                    ![EVENT_CANCELED].includes(event.type) &&
                    (event.user_id === therapist._id || event.user?._id === therapist._id) &&
                    event.isSubstituted
            )

            .map(event => ({
                ...event,
                color: colorIndexMap[event?.user_id || event?.user?._id]?.session
            }));

        return therapistEvents.concat(therapistBookedEvents);
    });

    return { events, therapists };
};
